/**
 * Shared "centre filter" state for every admin page.
 *
 * Single source of truth for the value rendered in per-section dropdowns
 * (Bookings, Customers, Staff, Horarios, Historial, Inicio, Calendar). The
 * sidebar global switcher is gone — pages own the UI but share the state.
 *
 * Storage: `sessionStorage` so the choice survives reloads inside the same
 * tab but resets on tab close (matches the user's mental model of "this is
 * a per-session context, not a permanent preference").
 *
 * The `X-Center-Id` header injection in `apiFetch` (see `api/client.ts`)
 * reads the same storage key. When the value is `'all'` no header is sent
 * and the backend falls back to the role's allowed-set behaviour:
 *   - superadmin → all active centres
 *   - center_admin → adminCenterIds ∪ legacy cid
 *   - staff → user.cid (single centre)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useCenters } from './useAdminResources';
import type { Center } from '@api/types';

export const ACTIVE_CENTER_STORAGE_KEY = 'admin_active_center_id';
/** Sentinel value meaning "no centre scoping" (`X-Center-Id` header skipped). */
export const ALL_CENTERS = 'all';
/** `'all'` means "no scoping"; any other string is a centre id. */
export type ActiveCenterValue_Id = string;

interface ActiveCenterValue {
  activeCenterId: ActiveCenterValue_Id;
  setActiveCenterId: (id: ActiveCenterValue_Id) => void;
  /** Centres the current user can switch between. */
  availableCenters: { id: string; name: string }[];
  /** True while `availableCenters` is being fetched. */
  isLoading: boolean;
  /** True for superadmin only. Kept for back-compat with one or two consumers. */
  isSuper: boolean;
  /** Hide the dropdown when there's nothing to pick. */
  canSwitch: boolean;
  /** Does the dropdown surface an "all centres" option? Hidden for staff. */
  supportsAll: boolean;
}

const ActiveCenterContext = createContext<ActiveCenterValue | null>(null);

function readStored(): ActiveCenterValue_Id | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_CENTER_STORAGE_KEY);
    if (!raw) return null;
    return raw === ALL_CENTERS ? ALL_CENTERS : raw;
  } catch {
    return null;
  }
}

function writeStored(value: ActiveCenterValue_Id | null): void {
  try {
    if (value === null) sessionStorage.removeItem(ACTIVE_CENTER_STORAGE_KEY);
    else sessionStorage.setItem(ACTIVE_CENTER_STORAGE_KEY, value);
  } catch {
    /* sessionStorage may be unavailable in some embedded contexts */
  }
}

export function ActiveCenterProvider({ children }: { children: ReactNode }) {
  const { user, hasRole } = useAuth();
  const qc = useQueryClient();
  const isSuper = hasRole('superadmin');
  const isCenterAdmin = hasRole('center_admin');
  const isStaff = hasRole('staff');

  // superadmin → /api/admin/centers (all active). center_admin → same endpoint
  // (backend fans out to their adminCenterIds). staff → empty for now; their
  // single centre comes from `user.centerId` and the dropdown is hidden.
  const enableCentersFetch = isSuper || isCenterAdmin;
  const { data: centersData = [], isLoading } = useCenters(enableCentersFetch);

  const supportsAll = !isStaff;

  // Default for super/center_admin is "all"; for staff it's their assigned
  // centre. We hydrate from sessionStorage first so refreshes preserve the
  // last choice within the tab.
  const initialValue: ActiveCenterValue_Id = (() => {
    const stored = readStored();
    if (stored !== null) return stored;
    if (isStaff && user?.centerId) return user.centerId;
    return ALL_CENTERS;
  })();

  const [activeCenterId, setActiveCenterIdState] = useState<ActiveCenterValue_Id>(initialValue);

  // When the centres list arrives, drop a stored id that's no longer in the
  // user's allowed set (e.g. a centre got deactivated or unassigned).
  useEffect(() => {
    if (activeCenterId === ALL_CENTERS) return;
    if (centersData.length === 0) return;
    if (!centersData.some((c) => c.id === activeCenterId)) {
      const next: ActiveCenterValue_Id = supportsAll
        ? ALL_CENTERS
        : (centersData[0]?.id ?? ALL_CENTERS);
      setActiveCenterIdState(next);
      writeStored(next);
    }
  }, [activeCenterId, centersData, supportsAll]);

  const setActiveCenterId = useCallback(
    (id: ActiveCenterValue_Id) => {
      const next = !supportsAll && id === ALL_CENTERS
        // Staff can't pick "all" — coerce to their assigned centre.
        ? (user?.centerId ?? ALL_CENTERS)
        : id;
      setActiveCenterIdState(next);
      writeStored(next);
      // Blunt but correct: every centre-scoped query needs to refetch with the
      // new X-Center-Id header (or lack thereof). Switching is user-driven and
      // infrequent.
      void qc.invalidateQueries();
    },
    [supportsAll, user?.centerId, qc],
  );

  const availableCenters = useMemo(
    () => centersData.map((c: Center) => ({ id: c.id, name: c.name })),
    [centersData],
  );

  const canSwitch = isStaff
    ? false  // staff dropdown hidden until multi-centre support lands
    : availableCenters.length > 1;

  const value: ActiveCenterValue = {
    activeCenterId,
    setActiveCenterId,
    availableCenters,
    isLoading,
    isSuper,
    canSwitch,
    supportsAll,
  };

  return <ActiveCenterContext.Provider value={value}>{children}</ActiveCenterContext.Provider>;
}

export function useActiveCenter(): ActiveCenterValue {
  const ctx = useContext(ActiveCenterContext);
  if (!ctx) {
    throw new Error('useActiveCenter must be used inside <ActiveCenterProvider>');
  }
  return ctx;
}
